import { del, get, post, put } from 'lib/actionHelpers.js';

import { LEADLOOP_FACEBOOK_CLIENT_ID } from '../../lib/constants.js';
import pluck from '../../lib/pluck.js';

export const patchPipelineStageOrder = (pipelineId, order, callback) => {
  return post(`/pipeline/${pipelineId}/reorder-stages`, {
    data: { order },
    action: 'PATCH_PIPELINE_STAGE_ORDER',
    callback,
  });
};

export const patchPipeline = ({ pipelineId, name, default_won_stage }, callback) => {
  return post(`/pipeline/${pipelineId}/update`, {
    data: { name, default_won_stage },
    action: 'PATCH_PIPELINE_NAME',
    callback,
  });
};

export const patchStageIsMilestone = (pipelineId, stageId, isMilestone, callback) => {
  return post(`/pipeline/${pipelineId}/stage/${stageId}/update`, {
    data: { is_milestone: isMilestone },
    action: 'PATCH_STAGE_IS_MILESTONE',
    callback,
  });
};

export const patchPipelineStage = ({ pipelineId, stageId, name, milestoneTarget, callback }) => {
  return post(`/pipeline/${pipelineId}/stage/${stageId}/update`, {
    data: {
      name,
      milestone_target: milestoneTarget,
    },
    action: 'PATCH_PIPELINE_STAGE',
    callback,
  });
};

export const postRecommendedAction = ({ serviceProviderId, pipelineId, stageId, actionType, index }) => {
  return post(`/pipeline/${pipelineId}/stage/${stageId}/recommended-action`, {
    data: {
      service_provider_id: serviceProviderId,
      action_order: index,
      action_type: actionType,
      wait_minutes: 1,
    },
    action: 'POST_RECOMMENDED_ACTION',
  });
};

export const patchRecommendedAction = (data = {}) => {
  return put(`/pipeline/${data.pipelineId}/stage/${data.stageId}/recommended-action`, {
    data: {
      action_id: data.id,
      service_provider_id: data.serviceProviderId,
      action_order: data.index,
      action_type: data.actionType,
      action_properties: data.actionProperties,
      template_id: data.templateId,
      action_notes: data.actionNotes,
      wait_minutes: data.waitMinutes,
    },
    action: 'PATCH_RECOMMENDED_ACTION',
  });
};

export const deleteRecommendedAction = ({ id, serviceProviderId, pipelineId, stageId }) => {
  return del(`/pipeline/${pipelineId}/stage/${stageId}/recommended-action`, {
    data: {
      action_id: id,
      service_provider_id: serviceProviderId,
    },
    action: 'DELETE_RECOMMENDED_ACTION',
  });
};

export const postPipelineStage = (pipelineId, name, callback) => {
  return put(`/pipeline/${pipelineId}/add-stage`, {
    data: { name },
    action: 'POST_PIPELINE_STAGE',
    callback,
  });
};

export const postPipeline = (data, callback) => {
  return put(`/pipeline/create`, {
    data,
    action: 'POST_PIPELINE',
    callback,
  });
};

export const getPlatformRegistrations = (data, callback) => {
  return get(`/service-provider/provider/${data.serviceProviderId}/platform-registrations`, {
    action: 'GET_PLATFORM_REGISTRATIONS',
    callback,
  });
};

export const postPlatformRegistration = (data, callback) => {
  return post(`/service-provider/provider/${data.serviceProviderId}/platform-registration`, {
    data: {
      platform_id: data.platformId,
      name: data.name,
      email_address: data.emailAddress,
      service_location_id: data.location,
      status: data.status,
      authentication_token: data.authenticationToken,
      authentication_password: data.authenticationPassword,
      authentication_id: data.authenticationId,
      provider_profile_id: data.providerProfileId,
      platform_provider_id: data.platformProviderId,
      secret: data.secret,
      extended_properties: data.extendedProperties,
    },
    action: 'POST_PLATFORM_REGISTRATION',
    callback,
  });
};

export const putPlatformRegistration = (data, callback) => {
  return put(`/service-provider/provider/${data.serviceProviderId}/platform-registration/${data.id}`, {
    data: {
      platform_id: data.platformId,
      name: data.name,
      email_address: data.emailAddress,
      service_location_id: data.location,
      status: data.status,
      authentication_token: data.authenticationToken,
      authentication_password: data.authenticationPassword,
      authentication_id: data.authenticationId,
      provider_profile_id: data.providerProfileId,
      user_id: data.userId,
      platform_provider_id: data.platformProviderId,
      secret: data.secret,
      deleted: data.deleted,
      extended_properties: data.extendedProperties,
    },
    action: 'PUT_PLATFORM_REGISTRATION',
    callback,
  });
};

export const postPlatformRegistrationsSync = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/platform-registrations-sync`, {
    data: pluck(data, ['platformRegistrationId', 'syncType', 'dataType', 'fieldMappings', 'dataMappings'], 'snake'),
    callback,
  });
};

export const putPlatformRegistrationsSync = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/platform-registrations-sync`, {
    data: pluck(data, ['id', 'syncType', 'dataType', 'fieldMappings', 'dataMappings'], 'snake'),
    callback,
  });
};

export const postPlatformRegistrationAddition = (serviceProviderId, data, callback) => {
  return put(`/service-provider/provider/${serviceProviderId}/add-platform-registration`, {
    data,
    action: 'POST_PLATFORM_REGISTRATION',
    callback: callback,
  });
};

export const deletePlatformRegistration = (data, callback) => {
  return del(`/service-provider/provider/${data.serviceProviderId}/platform-registration/${data.id}`, {
    data: {
      hard: data.hard,
    },
    action: 'DELETE_PLATFORM_REGISTRATION',
    callback,
  });
};

export const postLocation = (data, callback) => {
  return put('/service-location/create', {
    data: pluck(data, ['serviceProviderId', 'serviceLocationName', 'timezone', 'addressLine1', 'addressLine2', 'defaultTrackedPhoneId', 'city', 'state', 'zipCode', 'countryCode'], 'snake'),
    action: 'POST_LOCATION',
    callback,
  });
};

export const putLocation = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/location`, {
    data: pluck(data, ['id', 'serviceProviderId', 'serviceLocationName', 'alternateBusinessName', 'timezone', 'addressLine1', 'addressLine2', 'defaultTrackedPhoneId', 'city', 'state', 'zipCode', 'countryCode'], 'snake'),
    action: 'PUT_LOCATION',
    callback,
  });
};

export const deletePipelineStage = (pipelineId, stageId, callback) => {
  return del(`/pipeline/${pipelineId}/stage/${stageId}/delete`, {
    action: 'DELETE_PIPELINE_STAGE',
    callback,
  });
};

export const postServiceProviderService = (data, callback) => {
  return put(`/service-provider/provider/${data.serviceProviderId}/add-service-offered`, {
    data: {
      service_name: data.serviceName,
      label: data.label,
      service_multiplier: parseFloat(data.serviceMultiplier),
      average_value: data.serviceAverageValue,
    },
    action: 'POST_SERVICE_PROVIDER_SERVICE',
    callback,
  });
};

export const putServiceProviderService = (data, callback) => {
  return post(`/service-provider/provider/${data.serviceProviderId}/service-offered/${data.id}/update`, {
    data: {
      service_name: data.serviceName,
      label: data.label,
      average_value: data.serviceAverageValue,
      service_multiplier: parseFloat(data.serviceMultiplier),
    },
    action: 'PUT_SERVICE_PROVIDER_SERVICE',
    callback,
  });
};

export const getTwilioPhoneNumbers = (id, query, callback) => {
  return get(`/service-provider/provider/${id}/search-available-phone-numbers`, {
    query,
    action: 'GET_TWILIO_PHONE_NUMBERS',
    callback,
  });
};

export const postTwilioPhoneNumberProvision = (id, data, callback) => {
  return post(`/service-provider/provider/${id}/provision-new-phone-number`, {
    data,
    action: 'POST_TWILIO_PHONE_NUMBER_PROVISION',
    callback,
  });
};

export const postTemplate = ({ serviceProviderId, templateName, subject, body, serviceLocationId, type }) => {
  return post(`/service-provider/provider/${serviceProviderId}/communication-template`, {
    data: {
      type: type,
      template_name: templateName,
      subject: subject,
      service_location_id: serviceLocationId,
      body: body,
    },
    action: 'POST_TEMPLATE',
  });
};

export const patchTemplate = ({ id, serviceProviderId, templateName, subject, body, serviceLocationId, type }) => {
  return put(`/service-provider/provider/${serviceProviderId}/communication-template`, {
    data: {
      type: type,
      template_name: templateName,
      subject: subject,
      body: body,
      service_location_id: serviceLocationId,
      template_id: id,
    },
    action: 'PATCH_TEMPLATE',
  });
};

export const postUser = (data) => {
  return post(`/provider/${data.serviceProviderId}/user`, {
    data: pluck(data, ['title', 'firstName', 'middleName', 'lastName', 'userPhone', 'roleId', 'email', 'defaultTrackedPhoneId', 'sendNotificationEmail', 'serviceLocationId', 'password', 'destinationPhoneId'], 'snake'),
    action: 'POST_USER',
    callback: data.callback,
  });
};

export const putUser = (data) => {
  return put(`/provider/${data.serviceProviderId}/user`, {
    data: pluck(data, ['id', 'title', 'firstName', 'middleName', 'lastName', 'userPhone', 'defaultTrackedPhoneId', 'active', 'roleId', 'serviceLocationId'], 'snake'),
    action: 'PUT_USER',
    callback: data.callback,
  });
};

export const getFacebookLogin = ({ serviceProviderId, platformRegistrationId, platformId, location, name }) => {
  const redirectUri = `${window.location.origin}/oauth/callback`;
  const state = btoa(
    JSON.stringify({
      serviceProviderId: serviceProviderId,
      name,
      platformId,
      platformRegistrationId,
      location,
      platformCode: 'facebook',
      redirectUri: window.location.href,
    })
  );
  const scope = ['pages_manage_metadata', 'pages_show_list', 'pages_messaging', 'instagram_manage_messages', 'pages_read_engagement', 'instagram_basic'];
  window.location = `https://www.facebook.com/v13.0/dialog/oauth?client_id=${LEADLOOP_FACEBOOK_CLIENT_ID}&redirect_uri=${redirectUri}&state=${state}&scope=${scope.join(',')}`;
};

export const getFacebookAccessToken = (code, callback) => {
  return get('/integration/facebook/access-token', {
    query: {
      code,
    },
    callback,
  });
};
export const postDestinationPhoneNumber = ({ serviceProviderId, phoneNumber, serviceLocationId, userId, phoneExt, waitSeconds, label, callback }) => {
  return post(`/service-provider/provider/${serviceProviderId}/destination-phone-number`, {
    data: {
      phone_number: phoneNumber,
      service_location_id: serviceLocationId,
      user_id: userId,
      phone_ext: phoneExt,
      wait_seconds: waitSeconds,
      label: label,
    },
    callback,
    action: 'POST_DESTINATION_PHONE',
  });
};

export const putDestinationPhoneNumber = ({ serviceProviderId, id, phoneNumber, serviceLocationId, userId, phoneExt, waitSeconds, label, callback }) => {
  return put(`/service-provider/provider/${serviceProviderId}/destination-phone-number`, {
    data: {
      id: id,
      phone_number: phoneNumber,
      service_location_id: serviceLocationId,
      user_id: userId,
      phone_ext: phoneExt,
      wait_seconds: waitSeconds,
      label: label,
    },
    callback,
    action: 'PUT_DESTINATION_PHONE',
  });
};

export const getTwilioAvailablePhoneNumbers = ({ serviceProviderId, pattern, areaCode, countryCode, phoneType, callback }) => {
  return get(`/service-provider/provider/${serviceProviderId}/search-available-phone-numbers`, {
    query: {
      pattern,
      area_code: areaCode,
      country_code: countryCode,
      phone_type: phoneType || 'local',
    },
    callback,
  });
};

export const postTwilioPhoneNumberPurchase = ({ serviceProviderId, phoneNumber, destinationPhoneId, serviceLocationId, label, utmMedium, utmSource, utmCampaign, callback }) => {
  return post(`/service-provider/provider/${serviceProviderId}/provision-new-phone-number`, {
    data: {
      phone_number: phoneNumber,
      destination_phone_id: destinationPhoneId,
      service_location_id: serviceLocationId,
      label: label,
      utm_medium: utmMedium,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
    },
    callback,
    action: 'POST_TRACKED_PHONE',
  });
};

export const postTrackedPhoneNumber = ({ serviceProviderId, phoneNumber, destinationPhoneId, serviceLocationId, label, utmMedium, utmSource, utmCampaign, platformRegistrationId, callback }) => {
  return post(`/service-provider/provider/${serviceProviderId}/tracked-phone-number`, {
    data: {
      platform_registration_id: platformRegistrationId,
      phone_number: phoneNumber,
      destination_phone_id: destinationPhoneId,
      service_location_id: serviceLocationId,
      label: label,
      utm_medium: utmMedium,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
    },
    callback,
    action: 'POST_TRACKED_PHONE',
  });
};

export const putTrackedPhoneNumber = ({ id, serviceProviderId, phoneNumber, destinationPhoneId, serviceLocationId, label, utmMedium, utmSource, utmCampaign, platformRegistrationId, callback }) => {
  return put(`/service-provider/provider/${serviceProviderId}/tracked-phone-number`, {
    data: {
      id: id,
      platform_registration_id: platformRegistrationId,
      phone_number: phoneNumber,
      destination_phone_id: destinationPhoneId,
      service_location_id: serviceLocationId,
      label: label,
      utm_medium: utmMedium,
      utm_source: utmSource,
      utm_campaign: utmCampaign,
    },
    callback,
    action: 'POST_TRACKED_PHONE',
  });
};

export const getRoles = ({ serviceProviderId, callback }) => {
  return get(`/service-provider/provider/${serviceProviderId}/role`, {
    action: 'GET_ROLES',
    callback,
  });
};

export const postRole = ({ serviceProviderId, name, callback }) => {
  return post(`/service-provider/provider/${serviceProviderId}/role`, {
    data: { name },
    action: 'POST_ROLE',
    callback,
  });
};

export const putRole = ({ serviceProviderId, id, name, callback }) => {
  return put(`/service-provider/provider/${serviceProviderId}/role`, {
    data: { id, name },
    action: 'PUT_ROLE',
    callback,
  });
};

export const putRolePermission = ({ serviceProviderId, roleId, permissionId, active, callback }) => {
  return put(`/service-provider/provider/${serviceProviderId}/role-permission`, {
    data: {
      role_id: roleId,
      permission_id: permissionId,
      active,
    },
    action: 'PUT_ROLE',
    callback,
  });
};

export const postUserRole = ({ serviceProviderId, userId, roleId, callback }) => {
  return post(`/service-provider/provider/${serviceProviderId}/user-role`, {
    data: { user_id: userId, role_id: roleId },
    action: 'POST_USER_ROLE',
    callback,
  });
};

export const deleteUserRole = ({ serviceProviderId, userId, roleId, callback }) => {
  return del(`/service-provider/provider/${serviceProviderId}/user-role`, {
    data: { user_id: userId, role_id: roleId },
    action: 'POST_USER_ROLE',
    callback,
  });
};

export const getCustomUtmSources = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/utm-sources`, {
    action: 'GET_CUSTOM_UTM_SOURCES',
    callback,
  });
};

export const putUtmSource = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/utm-sources`, {
    data: { value: data.value, name: data.name, id: data.id },
    action: 'GET_CUSTOM_UTM_SOURCES',
    callback,
  });
};

export const postUtmSource = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/utm-sources`, {
    data: { value: data.value, name: data.name, id: data.id },
    action: 'GET_CUSTOM_UTM_SOURCES',
    callback,
  });
};

export const deleteUtmSource = (data, callback) => {
  return del(`/provider/${data.serviceProviderId}/utm-sources`, {
    data: { id: data.id },
    action: 'GET_CUSTOM_UTM_SOURCES',
    callback,
  });
};

export const getCustomUtmCampaigns = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/utm-campaigns`, {
    action: 'GET_CUSTOM_UTM_CAMPAIGNS',
    callback,
  });
};

export const putUtmCampaign = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/utm-campaigns`, {
    data: { value: data.value, name: data.name, id: data.id },
    action: 'GET_CUSTOM_UTM_CAMPAIGNS',
    callback,
  });
};

export const postUtmCampaign = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/utm-campaigns`, {
    data: { value: data.value, name: data.name, id: data.id },
    action: 'GET_CUSTOM_UTM_CAMPAIGNS',
    callback,
  });
};

export const deleteUtmCampaign = (data, callback) => {
  return del(`/provider/${data.serviceProviderId}/utm-campaigns`, {
    data: { id: data.id },
    action: 'GET_CUSTOM_UTM_CAMPAIGNS',
    callback,
  });
};

export const putUtmMapping = ({ serviceProviderId, callback }) => {
  return put(`/provider/${serviceProviderId}/utm`, {
    callback,
  });
};

export const getEmailSignatures = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/email-signatures`, {
    action: 'GET_EMAIL_SIGNATURES',
    callback,
  });
};

export const postEmailSignature = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/email-signatures`, {
    data: pluck(data, ['userId', 'serviceLocationId', 'emailSignature'], 'snake'),
    action: 'GET_EMAIL_SIGNATURES',
    callback,
  });
};

export const putEmailSignature = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/email-signatures`, {
    data: pluck(data, ['id', 'userId', 'serviceLocationId', 'emailSignature'], 'snake'),
    action: 'GET_EMAIL_SIGNATURES',
    callback,
  });
};

export const deleteEmailSignature = (data, callback) => {
  return del(`/provider/${data.serviceProviderId}/email-signatures`, {
    data: pluck(data, ['id'], 'snake'),
    action: 'GET_EMAIL_SIGNATURES',
    callback,
  });
};

export const getAutomatedReports = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/automated-reports`, {
    action: 'GET_AUTOMATED_REPORTS',
    callback,
  });
};

export const postAutomatedReport = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/automated-reports`, {
    data: pluck(data, ['serviceLocationId', 'reportType', 'toEmails', 'ccEmails', 'bccEmails', 'active'], 'snake'),
    action: 'GET_AUTOMATED_REPORTS',
    callback,
  });
};

export const putAutomatedReport = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/automated-reports`, {
    data: pluck(data, ['id', 'serviceLocationId', 'reportType', 'toEmails', 'ccEmails', 'bccEmails', 'active'], 'snake'),
    action: 'GET_AUTOMATED_REPORTS',
    callback,
  });
};

export const deleteAutomatedReport = (data, callback) => {
  return del(`/provider/${data.serviceProviderId}/automated-reports`, {
    data: pluck(data, ['id'], 'snake'),
    action: 'GET_AUTOMATED_REPORTS',
    callback,
  });
};

export const getIntegrationOauth = (data, callback) => {
  return get(`/integration/oauth2/authorize`, {
    query: pluck(data, ['serviceProviderId', 'redirectUrl'], 'snake'),
    callback,
  });
};

export const getOfficeHours = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/office-hours`, {
    action: 'GET_OFFICE_HOURS',
    callback,
  });
};

export const setOfficeHours = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/office-hours`, {
    action: 'SET_OFFICE_HOURS',
    callback,
    data: data.officeHours,
  });
};

export const getServicePersons = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/service-person`, {
    action: 'GET_SERVICE_PERSONS',
    callback,
  });
};

export const getServicePersonLocations = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/service-person/location`, {
    query: {
      service_person_id: data.servicePersonId,
    },
    action: 'GET_SERVICE_PERSON_LOCATIONS',
    callback,
  });
};

export const postServicePerson = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/service-person`, {
    data: pluck(data, ['serviceProviderId', 'userId', 'title', 'namePrefix', 'firstName', 'lastName', 'middleName', 'nameSuffix', 'initials', 'phone', 'email', 'description', 'active'], 'snake'),
    action: 'PUT_SERVICE_PERSON',
    callback,
  });
};

export const putServicePerson = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/service-person`, {
    data: pluck(data, ['id', 'serviceProviderId', 'userId', 'title', 'namePrefix', 'firstName', 'lastName', 'middleName', 'nameSuffix', 'initials', 'phone', 'email', 'description', 'active'], 'snake'),
    action: 'PUT_SERVICE_PERSON',
    callback,
  });
};

export const postServicePersonLocation = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/service-person/location`, {
    data: pluck(data, ['serviceProviderId', 'serviceLocationId', 'servicePersonId'], 'snake'),
    action: 'PUT_SERVICE_PERSON_LOCATION',
    callback,
  });
};

export const putServicePersonLocation = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/service-person/location`, {
    data: pluck(data, ['id', 'serviceProviderId', 'serviceLocationId', 'servicePersonId'], 'snake'),
    action: 'PUT_SERVICE_PERSON_LOCATION',
    callback,
  });
};

export const deleteServicePersonLocation = (data, callback) => {
  return del(`/provider/${data.serviceProviderId}/service-person/location`, {
    data: pluck(data, ['id'], 'snake'),
    action: 'DELETE_SERVICE_PERSON_LOCATION',
    callback,
  });
};

export const getApiKeys = (data, callback) => {
  return get(`/provider/${data.serviceProviderId}/api-key`, {
    action: 'GET_API_KEYS',
    callback,
  });
};

export const postApiKey = (data, callback) => {
  return post(`/provider/${data.serviceProviderId}/api-key`, {
    data: pluck(data, ['label'], 'snake'),
    action: 'GET_API_KEYS',
    callback,
  });
};

export const putApiKey = (data, callback) => {
  return put(`/provider/${data.serviceProviderId}/api-key`, {
    data: pluck(data, ['id', 'label'], 'snake'),
    action: 'GET_API_KEYS',
    callback,
  });
};

export const deleteApiKey = (data, callback) => {
  return del(`/provider/${data.serviceProviderId}/api-key`, {
    data: pluck(data, ['id'], 'snake'),
    action: 'GET_API_KEYS',
    callback,
  });
};
